import { ADMIN_COLORS } from '../constants/bookingConst';
import { ERROR_LOG_SOURCE_LABELS, buildErrorLogText, downloadTextFile } from '../utils/errorLogUtil';
import '../css/AdminErrorLogs.css';

const COLORS = ADMIN_COLORS;

/**
 * "Sep 21, 2026, 2:32:10 PM" — seconds included on purpose. Two entries a
 * second apart are the normal case when one failure cascades, and minute
 * precision makes them look simultaneous.
 * @param {number} ts
 * @returns {string}
 */
const formatFull = (ts) => (ts
  ? new Date(ts).toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit', second: '2-digit',
  })
  : '—');

/**
 * One row of the detail sheet. Renders nothing at all when the field is
 * empty, so a sparse entry reads as short rather than as a column of dashes.
 *
 * @param {object} props
 * @param {string} props.label
 * @param {*} props.value
 * @param {boolean} [props.mono] - render the value as preformatted text
 * @returns {JSX.Element|null}
 */
function DetailRow({ label, value, mono = false }) {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div className="ael-detail-row">
      <span className="ael-detail-label">{label}</span>
      {mono
        ? <pre className="ael-detail-pre">{value}</pre>
        : <span className="ael-detail-value">{value}</span>}
    </div>
  );
}

/**
 * The full record behind one row of the error table — everything the browser
 * knew at the moment it failed, including the stack, which is too wide to
 * show in the table itself.
 *
 * Not closed by a backdrop click, and carries an explicit close button
 * (CLAUDE.md > Behavior).
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {object|null} props.entry - the error log record
 * @param {Function} props.onClose
 * @param {string} [props.clubName] - written into a single-entry download
 * @returns {JSX.Element|null}
 */
function ErrorLogDetailModal({ isOpen, entry, onClose, clubName = '' }) {
  if (!isOpen || !entry) return null;

  const isWarn = entry.level === 'warn';

  /** Saves just this one record as a .txt, for pasting into a bug report. */
  const handleDownloadOne = () => {
    const text = buildErrorLogText([entry], { clubName, label: `entry-${entry.id}` });
    downloadTextFile(text, `error-${entry.id}.txt`);
  };

  return (
    <div className="ael-modal-overlay">
      <div className="ael-modal">
        <div className="ael-modal-header">
          <div className="ael-modal-heading">
            <span
              className="ael-level-pill"
              style={{
                color: isWarn ? COLORS.warning : COLORS.danger,
                background: `${isWarn ? COLORS.warning : COLORS.danger}1a`,
              }}
            >
              {isWarn ? 'Warning' : 'Error'}
            </span>
            <h3 className="ael-modal-title">
              {ERROR_LOG_SOURCE_LABELS[entry.source] || entry.source || 'Error'}
            </h3>
          </div>
          <button type="button" className="ael-modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="ael-modal-body">
          <DetailRow label="When" value={formatFull(entry.timestamp)} />
          <DetailRow label="Code" value={entry.code} />
          <DetailRow label="Message" value={entry.message} mono />
          <DetailRow label="Screen" value={entry.screen} />
          <DetailRow label="Page" value={entry.url} />
          <DetailRow label="Signed in as" value={entry.actor} />
          <DetailRow label="Browser session" value={entry.sessionId} />
          {/* Only shown when it actually repeated — see the dedupe window in
              errorLogUtil. A "+0" on every entry would be noise. */}
          <DetailRow
            label="Repeats"
            value={entry.repeatCount ? `${entry.repeatCount} more identical within a minute` : null}
          />
          <DetailRow label="Context" value={entry.context} mono />
          <DetailRow label="Browser" value={entry.userAgent} />
          <DetailRow label="Stack" value={entry.stack} mono />
        </div>

        <div className="ael-modal-footer">
          <button type="button" className="ael-btn ael-btn-ghost" onClick={handleDownloadOne}>
            Download this entry
          </button>
          <button type="button" className="ael-btn ael-btn-primary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default ErrorLogDetailModal;
